(() => {
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  const initLanding = (root) => {
    if (!root || root.dataset.pbs26Initialized === 'true') return;

    root.dataset.pbs26Initialized = 'true';

    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
    if (reducedMotion.matches) {
      root.classList.add('is-ready');
    } else {
      root.classList.add('is-enhanced');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => root.classList.add('is-ready'));
      });
    }

    const hero = root.querySelector('#hero');
    let heroFrame = 0;

    const updateHeroDepth = () => {
      heroFrame = 0;
      if (!hero) return;

      const rect = hero.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, -rect.top / Math.max(rect.height, 1)));
      root.style.setProperty('--pbs-hero-scroll', progress.toFixed(4));
    };

    const requestHeroDepth = () => {
      if (heroFrame) return;
      heroFrame = requestAnimationFrame(updateHeroDepth);
    };

    updateHeroDepth();
    window.addEventListener('scroll', requestHeroDepth, { passive: true });
    window.addEventListener('resize', requestHeroDepth, { passive: true });

    const journeyStage = root.querySelector('[data-pbs26-journey-stage]');
    const journeySteps = [...root.querySelectorAll('[data-pbs26-journey-step]')];
    const journeyMedia = [...root.querySelectorAll('[data-pbs26-stage-media]')];
    let activeJourneyStage = journeySteps.find((step) => step.classList.contains('is-active'))?.dataset.pbs26JourneyStep;
    let targetJourneyStage = activeJourneyStage;
    let journeyTransitioning = false;
    let journeyExitTimer = 0;
    let journeyFrame = 0;

    const showJourneyStage = (stage) => {
      journeyMedia.forEach((media) => {
        media.classList.remove('is-active', 'is-exiting');
      });
      journeyMedia.find((media) => media.dataset.pbs26StageMedia === stage)?.classList.add('is-active');
      activeJourneyStage = stage;
      journeyTransitioning = false;

      if (targetJourneyStage !== activeJourneyStage) beginJourneyTransition();
    };

    const beginJourneyTransition = () => {
      if (journeyTransitioning || !targetJourneyStage || targetJourneyStage === activeJourneyStage) return;

      if (reducedMotion.matches) {
        showJourneyStage(targetJourneyStage);
        return;
      }

      const outgoing = journeyMedia.find((media) => media.dataset.pbs26StageMedia === activeJourneyStage);
      if (!outgoing) {
        showJourneyStage(targetJourneyStage);
        return;
      }

      journeyTransitioning = true;
      outgoing.classList.add('is-exiting');
      window.clearTimeout(journeyExitTimer);
      journeyExitTimer = window.setTimeout(() => {
        outgoing.classList.remove('is-active', 'is-exiting');
        activeJourneyStage = undefined;
        requestAnimationFrame(() => {
          showJourneyStage(targetJourneyStage);
        });
      }, 240);
    };

    const setJourneyStage = (stage) => {
      if (!stage) return;
      targetJourneyStage = stage;
      journeySteps.forEach((step) => {
        step.classList.toggle('is-active', step.dataset.pbs26JourneyStep === stage);
      });
      beginJourneyTransition();
    };

    const updateJourneyStage = () => {
      journeyFrame = 0;
      if (!journeySteps.length || !journeyStage || window.innerWidth < 750) return;

      const focusLine = window.innerHeight * 0.5;
      const stepAtFocus = journeySteps.find((step) => {
        const rect = step.getBoundingClientRect();
        return rect.top <= focusLine && rect.bottom > focusLine;
      });

      if (stepAtFocus) {
        setJourneyStage(stepAtFocus.dataset.pbs26JourneyStep);
        return;
      }

      const closestStep = journeySteps.reduce((closest, step) => {
        const rect = step.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height * 0.5 - focusLine);
        return !closest || distance < closest.distance ? { step, distance } : closest;
      }, null);

      setJourneyStage(closestStep?.step.dataset.pbs26JourneyStep);
    };

    const requestJourneyStage = () => {
      if (journeyFrame) return;
      journeyFrame = requestAnimationFrame(updateJourneyStage);
    };

    updateJourneyStage();
    window.addEventListener('scroll', requestJourneyStage, { passive: true });
    window.addEventListener('resize', requestJourneyStage, { passive: true });

    const scrollCue = root.querySelector('.pbs26__scroll-cue');
    if (scrollCue && !reducedMotion.matches) {
      scrollCue.addEventListener('click', (event) => {
        const target = root.querySelector(scrollCue.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    if (!reducedMotion.matches && typeof Element.prototype.animate === 'function') {
      const faqAnimations = new WeakMap();

      root.querySelectorAll('.pbs26__faq-item').forEach((item) => {
        const summary = item.querySelector(':scope > summary');
        const content = item.querySelector(':scope > div');
        if (!summary || !content) return;

        const toggleFaqItem = () => {
          const previous = faqAnimations.get(item);
          const opening = previous ? !previous.opening : !item.open;
          const startHeight = item.getBoundingClientRect().height;

          previous?.height.cancel();
          previous?.content.cancel();

          item.open = true;
          item.classList.toggle('is-opening', opening);
          item.classList.toggle('is-closing', !opening);
          item.style.height = `${startHeight}px`;
          item.style.overflow = 'hidden';

          const endHeight = summary.getBoundingClientRect().height + (opening ? content.getBoundingClientRect().height : 0);
          const heightAnimation = item.animate(
            { height: [`${startHeight}px`, `${endHeight}px`] },
            { duration: opening ? 460 : 340, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
          );
          const contentAnimation = content.animate(
            opening
              ? [{ opacity: 0, transform: 'translate3d(0, -8px, 0)' }, { opacity: 1, transform: 'translate3d(0, 0, 0)' }]
              : [{ opacity: 1, transform: 'translate3d(0, 0, 0)' }, { opacity: 0, transform: 'translate3d(0, -6px, 0)' }],
            { duration: opening ? 380 : 220, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
          );
          const current = { opening, height: heightAnimation, content: contentAnimation };
          faqAnimations.set(item, current);

          heightAnimation.onfinish = () => {
            if (faqAnimations.get(item) !== current) return;
            faqAnimations.delete(item);
            item.open = opening;
            item.classList.remove('is-opening', 'is-closing');
            item.style.removeProperty('height');
            item.style.removeProperty('overflow');
            heightAnimation.cancel();
            contentAnimation.cancel();
          };
        };

        summary.addEventListener('click', (event) => {
          event.preventDefault();
          toggleFaqItem();
        });

        summary.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          toggleFaqItem();
        });
      });
    }

    const revealTargets = [...root.querySelectorAll('[data-pbs26-visit-visual], [data-pbs26-gift-still-life], [data-pbs26-final]')];
    let revealObserver;
    let revealFrame = 0;

    const revealElement = (element) => {
      if (element.classList.contains('is-revealed')) return;
      element.classList.add('is-revealed');
      revealObserver.unobserve(element);
    };

    revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealElement(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.18 }
    );

    revealTargets.forEach((element) => {
      revealObserver.observe(element);
    });

    const revealPassedTargets = () => {
      revealFrame = 0;
      const triggerLine = window.innerHeight * 0.88;
      revealTargets.forEach((element) => {
        if (element.getBoundingClientRect().top <= triggerLine) revealElement(element);
      });
    };

    const requestRevealCheck = () => {
      if (revealFrame) return;
      revealFrame = requestAnimationFrame(revealPassedTargets);
    };

    requestRevealCheck();
    window.addEventListener('scroll', requestRevealCheck, { passive: true });
    window.addEventListener('resize', requestRevealCheck, { passive: true });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-pbs26-section]').forEach(initLanding);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
